"use client";

/*
 * Path Builder — pick two PoPs, see how traffic actually travels.
 *
 * Replaces the former "Global Live Mesh" on the homepage. Everything shown
 * comes from src/data/network-paths.ts (topology + indicative RTT) and
 * src/data/service-availability.ts (what is sold where). Nothing is
 * simulated or presented as live.
 */

import { useMemo, useState } from "react";
import Link from "@/components/LocalizedLink";
import { useTranslation } from "@/lib/i18n";
import {
  CORRIDOR_INFO,
  DIVERSE_APPROACHES,
  PATH_NODES,
  PUBLIC_INTERNET_RTT_FROM_SOFIA,
  PUBLIC_INTERNET_SOURCE,
  nodeLabel,
  shortestPath,
  type Medium,
} from "@/data/network-paths";
import { isAvailable, SERVICE_LABELS, type ServiceId } from "@/data/service-availability";
import { NetworkSchematic, SchematicLegend, type SchematicView } from "./NetworkSchematic";

const BG_EDGE = new Set([
  "plovdiv", "stara-zagora", "burgas", "varna", "dobrich",
  "montana", "pleven", "blagoevgrad",
]);

/** Configurator only knows these PoPs as route endpoints. */
const CONFIGURATOR_POPS = new Set([
  "sofia", "frankfurt", "istanbul", "athens", "london", "tbilisi", "dubai",
  "yerevan", "agarak", "plovdiv", "stara-zagora", "burgas", "varna",
  "balchik", "dobrich", "montana", "pleven",
]);

const COPY = {
  en: {
    eyebrow: "Path Builder",
    heading: "See exactly how your traffic travels",
    sub: "Pick two points on the network. You get the physical route, an indicative round-trip time and what we can deliver at each end.",
    from: "From",
    to: "To",
    swap: "Swap",
    intl: "International",
    bg: "Bulgaria",
    rtt: "Indicative round trip",
    rttNote: "Fibre-path estimate, confirmed per order. Not a live measurement.",
    route: "Route",
    publicNet: "Same city pair over the public internet today",
    publicSrc: "Typical server-to-server ping",
    faster: (x: number) => `${x}× lower latency`,
    services: "Available at both ends",
    none: "Ask us — delivered per project",
    configure: "Configure this route",
    talk: "Talk to engineering",
    tapHint: "Tip: tap a station on the map to set the destination.",
    medium: { terrestrial: "terrestrial", subsea: "subsea", partner: "partner" } as Record<Medium, string>,
    routes: (n: number) => `${n} protected routes`,
    approaches: (n: number) => `${n} diverse approaches`,
    partner: "Final leg via a long-haul partner; RTT indicative.",
    mapTitle: "Schematic map of the Sofia Connect backbone",
  },
  bg: {
    eyebrow: "Планиране на трасе",
    heading: "Вижте точно откъде минава трафикът ви",
    sub: "Изберете две точки от мрежата. Получавате физическото трасе, ориентировъчно време за двупосочен път и какво можем да доставим във всеки край.",
    from: "От",
    to: "До",
    swap: "Размени",
    intl: "Международна",
    bg: "България",
    rtt: "Ориентировъчно RTT",
    rttNote: "Оценка по трасето на влакното, потвърждава се при поръчка. Не е измерване на живо.",
    route: "Трасе",
    publicNet: "Същата двойка градове през обществения интернет днес",
    publicSrc: "Типичен ping между сървъри",
    faster: (x: number) => `${x}× по-ниско закъснение`,
    services: "Налично в двата края",
    none: "Попитайте ни — доставя се по проект",
    configure: "Конфигурирай това трасе",
    talk: "Говорете с инженер",
    tapHint: "Съвет: докоснете станция на картата, за да я изберете за дестинация.",
    medium: { terrestrial: "наземно", subsea: "подводно", partner: "партньор" } as Record<Medium, string>,
    routes: (n: number) => `${n} защитени трасета`,
    approaches: (n: number) => `${n} независими подхода`,
    partner: "Последният участък е чрез партньор; RTT е ориентировъчно.",
    mapTitle: "Схема на магистралната мрежа на Sofia Connect",
  },
};

const SERVICES: ServiceId[] = ["ip-transit", "ethernet-l2vpn", "dwdm", "dark-fibre"];

export function PathBuilder({
  defaultFrom = "sofia",
  defaultTo = "tbilisi",
  headingLevel = "h2",
}: {
  defaultFrom?: string;
  defaultTo?: string;
  headingLevel?: "h1" | "h2";
}) {
  const { language } = useTranslation();
  const lang = language === "bg" ? "bg" : "en";
  const c = COPY[lang];
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [view, setView] = useState<SchematicView>(
    BG_EDGE.has(defaultTo) ? "bulgaria" : "international",
  );

  const path = useMemo(() => shortestPath(from, to), [from, to]);
  const pops = PATH_NODES.filter((n) => !n.transit);

  const publicRtt =
    from === "sofia"
      ? PUBLIC_INTERNET_RTT_FROM_SOFIA[to]
      : to === "sofia"
        ? PUBLIC_INTERNET_RTT_FROM_SOFIA[from]
        : undefined;

  const common = SERVICES.filter((s) => isAvailable(from, s) && isAvailable(to, s));
  const approaches = DIVERSE_APPROACHES[to];

  const configHref =
    CONFIGURATOR_POPS.has(from) && CONFIGURATOR_POPS.has(to)
      ? `/?p=dwdm&o=${from}&d=${to}#configurator`
      : "/#configurator";

  const pick = (id: string) => {
    if (id === from) return;
    setTo(id);
  };
  const setEndpoint = (which: "from" | "to", id: string) => {
    if (which === "from") {
      if (id === to) setTo(from);
      setFrom(id);
    } else {
      if (id === from) setFrom(to);
      setTo(id);
    }
    if (BG_EDGE.has(id)) setView("bulgaria");
  };

  const H = headingLevel;
  const selectCls =
    "w-full rounded-lg border border-[var(--signal-line)] bg-[var(--signal-surface-2)] px-3 py-2.5 text-sm text-[var(--signal-ink)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]";

  return (
    <section id="path-builder" className="scroll-mt-20 py-20 px-4 sm:px-6 lg:px-8" aria-labelledby="path-builder-heading">
      <div className="mx-auto max-w-7xl">
        <div className="max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-eyebrow">{c.eyebrow}</p>
          <H id="path-builder-heading" className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-[var(--signal-ink)]">
            {c.heading}
          </H>
          <p className="mt-3 text-base text-[var(--signal-ink-dim)]">{c.sub}</p>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
          {/* Map */}
          <div className="min-w-0 self-start rounded-2xl border border-[var(--signal-line)] bg-[var(--signal-void)] p-4 sm:p-6">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div role="tablist" aria-label={c.route} className="inline-flex rounded-lg border border-[var(--signal-line)] p-1">
                {(["international", "bulgaria"] as const).map((v) => (
                  <button
                    key={v}
                    role="tab"
                    aria-selected={view === v}
                    onClick={() => setView(v)}
                    className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                      view === v
                        ? "bg-[var(--accent)] text-[var(--on-accent)]"
                        : "text-[var(--signal-ink-dim)] hover:text-[var(--signal-ink)]"
                    }`}
                  >
                    {v === "international" ? c.intl : c.bg}
                  </button>
                ))}
              </div>
            </div>
            {/* On phones the schematic keeps a readable size and scrolls sideways. */}
            <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
              <div className="min-w-[640px] sm:min-w-0">
                <NetworkSchematic
                  view={view}
                  lang={lang}
                  highlight={path?.segments ?? []}
                  endpoints={[from, to]}
                  onNodeClick={pick}
                  title={c.mapTitle}
                />
              </div>
            </div>
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <SchematicLegend lang={lang} />
              <p className="text-xs text-[var(--signal-ink-faint)]">{c.tapHint}</p>
            </div>
          </div>

          {/* Panel */}
          <div className="flex flex-col gap-4">
            <div className="rounded-2xl border border-[var(--signal-line)] bg-[var(--signal-surface)] p-5">
              <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-2">
                <label className="text-xs text-[var(--signal-ink-dim)]">
                  {c.from}
                  <select className={`${selectCls} mt-1`} value={from} onChange={(e) => setEndpoint("from", e.target.value)}>
                    {pops.map((n) => (
                      <option key={n.id} value={n.id}>{n.label[lang]}</option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  onClick={() => { setFrom(to); setTo(from); }}
                  aria-label={c.swap}
                  className="mb-1 rounded-lg border border-[var(--signal-line)] px-2.5 py-2 text-[var(--signal-ink-dim)] hover:text-[var(--signal-ink)]"
                >
                  ⇄
                </button>
                <label className="text-xs text-[var(--signal-ink-dim)]">
                  {c.to}
                  <select className={`${selectCls} mt-1`} value={to} onChange={(e) => setEndpoint("to", e.target.value)}>
                    {pops.map((n) => (
                      <option key={n.id} value={n.id}>{n.label[lang]}</option>
                    ))}
                  </select>
                </label>
              </div>
            </div>

            {path && (
              <div className="rounded-2xl border border-[var(--signal-line)] bg-[var(--signal-surface)] p-5" aria-live="polite">
                <p className="text-xs uppercase tracking-wider text-[var(--signal-ink-dim)]">{c.rtt}</p>
                <p className="mt-1 font-mono text-4xl font-semibold text-[var(--signal-ink)]">
                  ≈ {path.rttMs}
                  <span className="ml-1 text-lg text-[var(--signal-ink-dim)]">ms</span>
                </p>
                <p className="mt-1 text-xs text-[var(--signal-ink-faint)]">{c.rttNote}</p>

                {publicRtt !== undefined && (
                  <div className="mt-5">
                    <p className="text-xs text-[var(--signal-ink-dim)]">{c.publicNet}</p>
                    <div className="mt-2 space-y-1.5">
                      <Bar label="Sofia Connect" value={path.rttMs} max={Math.max(publicRtt, path.rttMs)} color="var(--accent)" />
                      <Bar label={lang === "bg" ? "Интернет" : "Internet"} value={publicRtt} max={Math.max(publicRtt, path.rttMs)} color="var(--signal-ink-faint)" />
                    </div>
                    {publicRtt / path.rttMs >= 1.5 && (
                      <p className="mt-2 text-sm font-semibold text-[var(--accent)]">
                        {c.faster(Math.round((publicRtt / path.rttMs) * 10) / 10)}
                      </p>
                    )}
                    <p className="mt-1 text-[11px] text-[var(--signal-ink-faint)]">
                      {c.publicSrc}:{" "}
                      <a href={PUBLIC_INTERNET_SOURCE.url} target="_blank" rel="noopener noreferrer" className="underline">
                        {PUBLIC_INTERNET_SOURCE.name}
                      </a>
                      , {PUBLIC_INTERNET_SOURCE.period[lang]}
                    </p>
                  </div>
                )}
              </div>
            )}

            {path && (
              <div className="rounded-2xl border border-[var(--signal-line)] bg-[var(--signal-surface)] p-5">
                <p className="text-xs uppercase tracking-wider text-[var(--signal-ink-dim)]">{c.route}</p>
                <ol className="mt-3 space-y-0">
                  {path.nodes.map((n, i) => {
                    const seg = path.segments[i];
                    return (
                      <li key={n}>
                        <p className="text-sm font-semibold text-[var(--signal-ink)]">{nodeLabel(n, lang)}</p>
                        {seg && (
                          <div className="my-1 ml-1 flex gap-3 border-l-4 py-1 pl-3" style={{ borderColor: CORRIDOR_INFO[seg.corridor].color }}>
                            <p className="text-xs text-[var(--signal-ink-dim)]">
                              {c.medium[seg.medium]} · {seg.rttMs} ms
                              {seg.routes && seg.routes > 1 ? ` · ${c.routes(seg.routes)}` : ""}
                              {seg.note ? ` · ${seg.note[lang]}` : ""}
                            </p>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ol>
                {approaches && from === "sofia" && !path.segments.some((s) => (s.routes ?? 1) > 1 && s.b === to) && (
                  <p className="mt-3 text-xs text-[var(--accent)]">{c.approaches(approaches)}</p>
                )}
                {path.viaPartner && <p className="mt-3 text-xs text-[var(--signal-ink-faint)]">{c.partner}</p>}
              </div>
            )}

            <div className="rounded-2xl border border-[var(--signal-line)] bg-[var(--signal-surface)] p-5">
              <p className="text-xs uppercase tracking-wider text-[var(--signal-ink-dim)]">{c.services}</p>
              <p className="mt-2 text-sm text-[var(--signal-ink)]">
                {common.length ? common.map((s) => SERVICE_LABELS[s][lang]).join(" · ") : c.none}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link
                  href={configHref}
                  className="rounded-lg bg-[var(--accent)] px-4 py-2.5 text-sm font-semibold text-[var(--on-accent)] hover:opacity-90"
                >
                  {c.configure}
                </Link>
                <Link
                  href="/contact"
                  className="rounded-lg border border-[var(--signal-line)] px-4 py-2.5 text-sm font-semibold text-[var(--signal-ink)] hover:border-[var(--accent)]"
                >
                  {c.talk}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Bar({ label, value, max, color }: { label: string; value: number; max: number; color: string }) {
  const pct = Math.max(4, Math.round((value / max) * 100));
  return (
    <div className="grid grid-cols-[96px_1fr_56px] items-center gap-2 text-xs">
      <span className="text-[var(--signal-ink-dim)]">{label}</span>
      <span className="h-2.5 rounded-full bg-[var(--signal-surface-2)]">
        <span className="block h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </span>
      <span className="text-right font-mono text-[var(--signal-ink)]">{value} ms</span>
    </div>
  );
}
